package one.healthium.log_food.ui.find_food

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import one.healthium.featureconfig.domain.CoreFeatureConfigKeys
import one.healthium.featureconfig.domain.FeatureConfigInteractor
import one.healthium.log_food.domain.FoodRepository
import one.healthium.log_food.ui.mappers.FoodUiMapper
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.vm.BaseViewModel
import one.primehealth.app.TargetPlatform
import one.primehealth.app.targetPlatform

class FindFoodViewModel(
    private val foodRepository: FoodRepository,
    private val foodUiMapper: FoodUiMapper,
    private val featureConfigInteractor: FeatureConfigInteractor,
) : BaseViewModel() {

    private val _state = MutableStateFlow(FindFoodUiState())
    val state = _state.asStateFlow()

    val events = EventProducer<FindFoodEvents>()

    init {
        launch {
            val isScannerSupported = targetPlatform() != TargetPlatform.Web
            val isScannerEnabled = featureConfigInteractor.isEnabled(CoreFeatureConfigKeys.EnableLogFoodScanner)
            _state.update { state -> state.copy(isScannerEnabled = isScannerSupported && isScannerEnabled) }
        }

        _state
            .map { it.searchQuery.trim() }
            .debounce(400)
            .distinctUntilChanged()
            .mapLatest { searchQuery ->
                if (searchQuery.isNotBlank()) {
                    foodRepository.suggestions(searchQuery)
                } else {
                    emptyList()
                }
            }
            .map { foodSuggestions ->
                foodSuggestions.map { foodUiMapper.toUiModel(it) }
            }
            .onEach {
                _state.update { state ->
                    state.copy(
                        foodSuggestions = it,
                        foods = null,
                    )
                }
            }
            .launch()
    }

    fun onQueryChanged(query: String) {
        _state.value = _state.value.copy(searchQuery = query)
    }

    fun onPerformSearch() {
        performSearch(_state.value.searchQuery)
    }

    private fun performSearch(searchQuery: String) {
        if (searchQuery.isEmpty()) return

        launch {
            try {
                _state.update { state -> state.copy(loading = true) }
                val foodList = foodRepository.searchFoodByName(searchQuery).map {
                    foodUiMapper.toUiModel(it)
                }
                _state.update { state ->
                    state.copy(
                        loading = false,
                        foods = foodList,
                        foodSuggestions = null,
                    )
                }
            } catch (e: Exception) {
                Logger.d("failed to fetch foods for query: $searchQuery", e)
                _state.update { state ->
                    state.copy(
                        loading = false,
                        foods = emptyList(),
                    )
                }
            }
        }
    }

    fun onBarcodeScanned(barcode: String?) {
        if (barcode.isNullOrEmpty()) return

        launch {
            try {
                _state.update { state -> state.copy(loading = true) }
                val foodList = foodRepository.searchFoodByCode(barcode).map {
                    foodUiMapper.toUiModel(it)
                }
                _state.update { state ->
                    state.copy(
                        loading = false,
                        foods = foodList,
                        foodSuggestions = null,
                    )
                }
            } catch (e: Exception) {
                Logger.d("failed to fetch foods for code: $barcode", e)
            }
        }
    }
}
